import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { stackedPair } from '../kits/stackKit';
import type { TextMeasurer } from '../kits/specs';
import { estimateMeasure } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Stacked fraction — the M0 micro-object proof recipe over stackKit
 * (CATALOG F1). Top number over a rule over a bottom number, with THE
 * FRACTION BAR LAW (stackKit.stackedPair): the bar re-derives to
 * max(top, bottom) width + overhang on every edit, so 1/2 and 13/100 both
 * look typeset.
 *
 * Editing surfaces, in the order teachers find them:
 *   • dblclick the top or bottom number ON canvas — the numbers carry
 *     data.tableCell = {kind:'param', key} tags (stamped by assemble.ts), so
 *     the smart-table overlay editor writes the committed text back into
 *     params and regenerates in place: transform preserved, ONE history
 *     entry.
 *   • the Smart Objects panel form (Apply) — the standard schema path.
 *
 * Emits FabricText + Line only, grouped and tagged {tool,version,params} —
 * plain fabric JSON, vector at any export dpi by construction.
 *
 * Blank slots are MEANINGFUL: clearing the top number prints an empty slot
 * over the bar (fill-in fractions), and the bar sizes to the remaining slot.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern) — params arrive
// untrusted (user-typed or replayed from stored JSON); generate() must never
// throw.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= blank slot). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available (the browser paths:
// insert and Apply both ensureFont the family first, so the measure sees the
// real face), estimate fallback elsewhere (SSR/node safety).
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

/** Ink + face defaults shared with the other math manipulatives (math.ts). */
const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';

/**
 * Internal render size. The glyphs are vector text — the group scales to
 * ~35% of the workspace on insert (addSmartObject) and prints crisp at any
 * size; a generous em keeps stroke/spacing ratios stable.
 */
const FONT_SIZE = 120;

const fraction: ToolRecipe = {
  id: 'fraction',
  domain: 'math',
  tags: ['fractions', 'notation', 'stacked-fraction', 'math-notation'],
  name: 'Fraction',
  category: 'math',
  version: 1,
  description:
    'A stacked fraction — top number over a bar that sizes itself to the widest number. Double-click a number on the poster to change it.',
  defaults: {
    numerator: '1',
    denominator: '2',
    barWeight: 8,
    color: INK,
    fontFamily: DEFAULT_FONT,
  },
  schema: [
    {
      key: 'numerator',
      label: 'Top number',
      type: 'text',
      help: 'The numerator. Leave empty for a fill-in slot.',
    },
    {
      key: 'denominator',
      label: 'Bottom number',
      type: 'text',
      help: 'The denominator. Leave empty for a fill-in slot.',
    },
    {
      key: 'barWeight',
      label: 'Bar thickness',
      type: 'number',
      min: 2,
      max: 30,
      step: 1,
      section: 'Style',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
  ],
  // Smart Tools modal exemplar (SM-A, 2026-07-23): the two numbers ARE the
  // fraction — hero them; Style stays a single tidy card.
  modal: { hero: ['numerator', 'denominator'] },
  // Honest band values: the vinculum thins as manipulative chunkiness gives
  // way to notation (K-2 blocky Fredoka + fat bar → 9-12 slim academic bar).
  gradeBands: {
    K2: { barWeight: 14, fontFamily: 'Fredoka' },
    G35: { barWeight: 10, fontFamily: 'Nunito' },
    G68: { barWeight: 8, fontFamily: 'Lexend' },
    G912: { barWeight: 5, fontFamily: 'Arial' },
  },
  async generate(params) {
    const numerator = rawStr(params.numerator, '1');
    const denominator = rawStr(params.denominator, '2');
    const barWeight = clamp(num(params.barWeight, 8), 1, 60);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);

    const block = stackedPair({
      numerator,
      denominator,
      fontSize: FONT_SIZE,
      barWeight,
      color,
      fontFamily: family,
      measure: makeMeasurer(family),
      numeratorParam: 'numerator',
      denominatorParam: 'denominator',
    });

    // Children carry absolute coords; Group derives the bbox and the caller
    // places it (mirrors every other recipe).
    const group = new Group(assembleSpecs(block.specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, fraction, params);
  },
};

registerRecipe(fraction);
